import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useState, useRef, useCallback } from 'react';
import { useWindowSize } from '@hzdg/windowsize-monitor';
import ResizeObservable from '@hzdg/resize-observable';
import useRefCallback from '@hzdg/use-ref-callback';
import memoizeOne from 'memoize-one'; // We really would just like to use DOMRect as our type here, but due to
// the new nature of the API and the polyfill behavior, The DOMRect interface
// isn't always fully implemented. in particular, the `toJSON()` method is
// often missing. Since we aren't using it directly anyway, we just omit it
// from our expected type.

var INITIAL_ELEMENT_SIZE = {
  x: 0,
  y: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: 0,
  height: 0,
  borderBoxSize: {
    blockSize: 0,
    inlineSize: 0
  },
  contentBoxSize: {
    blockSize: 0,
    inlineSize: 0
  }
};
var INITIAL_VIEW_SIZE = {
  width: 0,
  height: 0
};
function shallowEqual(a, b) {
  if (a === b) return true;
  for (var k in a) {
    if (a[k] !== b[k]) {
      return false;
    }
  }
  return true;
}
/**
 * `getSize` calculates a new `Size` from a `DOMRect` and viewport size.
 */

var getSize = memoizeOne(function getSize(elementSize, viewSize) {
  return Object.freeze({
    x: elementSize.x,
    y: elementSize.y,
    top: elementSize.top,
    right: elementSize.right,
    bottom: elementSize.bottom,
    left: elementSize.left,
    width: elementSize.width,
    height: elementSize.height,
    vw: Math.round(elementSize.width / viewSize.width * 100) / 100 || 0,
    vh: Math.round(elementSize.height / viewSize.height * 100) / 100 || 0,
    borderBoxSize: elementSize.borderBoxSize,
    contentBoxSize: elementSize.contentBoxSize
  });
}, function areInputsEqual(_ref, _ref2) {
  var _ref3 = _slicedToArray(_ref, 2),
    newElementSize = _ref3[0],
    newViewSize = _ref3[1];
  var _ref4 = _slicedToArray(_ref2, 2),
    lastElementSize = _ref4[0],
    lastViewSize = _ref4[1];
  return shallowEqual(newElementSize, lastElementSize) && shallowEqual(newElementSize.borderBoxSize, lastElementSize.borderBoxSize) && shallowEqual(newElementSize.contentBoxSize, lastElementSize.contentBoxSize) && shallowEqual(newViewSize, lastViewSize);
});
/**
 * `useForceUpdate` will return a function that, when called
 * will force the component to rerender.
 */

function useForceUpdate() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    flipUpdateBit = _useState2[1];
  var forceUpdate = useCallback(function forceUpdate() {
    flipUpdateBit(function (v) {
      return !v;
    });
  }, []);
  return forceUpdate;
}
function useSubscription(ref, handler) {
  var subscribed = useRef(null);
  var subscription = useRef(null);
  var subscriber = useRef(null);
  var subscribeIfNecessary = useCallback(function () {
    if (ref.current && handler) {
      subscribed.current = ref.current;
      subscriber.current = handler;
      subscription.current = ResizeObservable.create(ref.current).subscribe(handler);
    }
  }, [ref, handler]);
  var cleanup = useCallback(function () {
    if (subscription.current) subscription.current.unsubscribe();
    subscribed.current = null;
    subscription.current = null;
    subscriber.current = null;
  }, []); // Resubscribe when ref or handler changes.

  if (subscribed.current !== ref.current || subscriber.current !== handler) {
    cleanup();
    subscribeIfNecessary();
  } // Cleanup subscription on unmount.

  useEffect(function () {
    return cleanup;
  }, [cleanup]);
  return Boolean(subscribed.current);
}
/**
 * `useSize` is a React hook for components that care about their size.
 * It can be used statefully or not, and with an existing ref or not.
 *
 * @see https://hz-core.netlify.com/use-size
 */

function useSize(handlerOrProvidedRef, handler) {
  var changeHandler = useRef(null);
  var providedRef = null;
  if (typeof handlerOrProvidedRef === 'function') {
    changeHandler.current = handlerOrProvidedRef;
  } else if (_typeof(handlerOrProvidedRef) === 'object') {
    providedRef = handlerOrProvidedRef;
    if (typeof handler === 'function') {
      changeHandler.current = handler;
    }
  }
  var _useRefCallback = useRefCallback(null),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    ref = _useRefCallback2[0],
    setRef = _useRefCallback2[1];
  if (providedRef) setRef(providedRef.current);
  var viewSize = useRef(INITIAL_VIEW_SIZE);
  var elementSize = useRef(INITIAL_ELEMENT_SIZE); // Note: we use `setState` here to take advantage of the
  // init function so that we only calculate the initial size once.

  var _useState3 = useState(function () {
      return getSize(elementSize.current, viewSize.current);
    }),
    _useState4 = _slicedToArray(_useState3, 1),
    initialSize = _useState4[0];
  var size = useRef(initialSize);
  var subscribed = useRef(false);
  var forceUpdate = useForceUpdate();
  var handleSizeChange = useCallback(
  /**
   * `handleSizeChange` will update the current change handler
   * with a new `Size` whenever the observed element's size or
   * the viewport size changes.
   */
  function handleSizeChange() {
    if (!subscribed.current) return;
    var nextSize = getSize(elementSize.current, viewSize.current);
    if (size.current !== nextSize) {
      size.current = nextSize;
      var cb = changeHandler.current;
      if (typeof cb === 'function') {
        // If we have a callback, then we're in 'stateless' mode,
        // so just call it with the new size.
        cb(size.current);
      } else {
        // Otherwise, we're in 'stateful' mode, so we should rerender,
        // as the size has changed.
        forceUpdate();
      }
    }
  }, [forceUpdate]);
  var handleWindowSizeChange = useCallback(
  /**
   * `handleWindowSizeChange` will update the current view size
   * with a new `WindowSize` whenever it changes, and also
   * call `handleSizeChange` to update the container size.
   */
  function handleWindowSizeChange(windowSize) {
    viewSize.current = windowSize;
    handleSizeChange();
  }, [handleSizeChange]);
  useWindowSize(handleWindowSizeChange);
  var handleElementSizeChange = useCallback(
  /**
   * `handleElementSizeChange` will update the current element size
   * with a new `Size` whenever it changes, and also
   * call `handleSizeChange` to update the container size.
   */
  function handleElementSizeChange(newSize) {
    elementSize.current = newSize;
    handleSizeChange();
  }, [handleSizeChange]);
  subscribed.current = useSubscription(ref, handleElementSizeChange);
  if (!providedRef) {
    if (changeHandler.current) {
      return setRef;
    } else {
      return [size.current, setRef];
    }
  } else if (!changeHandler.current) {
    return size.current;
  }
}
export default useSize;